import React, { Component } from 'react';
import { arrayOf, bool, func, object, shape, string } from 'prop-types';
import { Form as FinalForm, Field } from 'react-final-form';
import classNames from 'classnames';

import { intlShape, injectIntl } from '../../../../util/reactIntl';
import { isMainSearchTypeKeywords } from '../../../../util/search';
import { createResourceLocatorString } from '../../../../util/routes';
import { createSlug } from '../../../../util/urlHelpers';
import { apiBaseUrl } from '../../../../util/api';
import { convertUnitToSubUnit, unitDivisor } from '../../../../util/currency';
import {
  searchAlgoliaAutocompleteClient,
  isClientSideAlgoliaConfigured,
  searchAlgoliaListingsClient,
} from '../../../../util/algoliaSearch';
import { propTypes } from '../../../../util/types';

import {
  Form,
  IconClose,
  LocationAutocompleteInput,
  OutsideClickHandler,
} from '../../../../components';

import IconSearchDesktop from './IconSearchDesktop';
import css from './TopbarSearchForm.module.css';

const identity = v => v;

const AUTOCOMPLETE_MIN_LENGTH = 3;
const AUTOCOMPLETE_DELAY = 350;
const AUTOCOMPLETE_BATCH_SIZE = 6;
const QUERY_SUGGESTION_BATCH_SIZE = 4;
const AUTOCOMPLETE_LISTING_SORT = 'relevance';
const AUTOCOMPLETE_GLOBAL = '@algolia/autocomplete-core';
const QUERY_SUGGESTIONS_SOURCE_ID = 'querySuggestions';
const LISTINGS_SOURCE_ID = 'listings';
const MOBILE_AUTOCOMPLETE_MEDIA_QUERY = '(max-width: 1023px)';

const isQuerySuggestionItem = suggestion => suggestion?.__sourceId === QUERY_SUGGESTIONS_SOURCE_ID;
const getQuerySuggestionText = suggestion =>
  `${suggestion?.query || suggestion?.suggestion || ''}`.trim();

const AutocompleteSuggestions = props => {
  const {
    activeSuggestionIndex,
    isLoadingMore,
    onSuggestionMouseDown,
    onSuggestionSelect,
    routeConfiguration,
    showSuggestions,
    suggestions,
    listingSuggestionCount,
    totalListingHits,
    isExpanded,
    onClose,
    onSeeMore,
    dropdownStyle,
    suggestionsScrollableRef,
    getSuggestionItemProps,
  } = props;

  if (!showSuggestions) {
    return null;
  }

  const hasSuggestions = suggestions.length > 0;
  const canSeeMore = listingSuggestionCount > 0 && listingSuggestionCount < totalListingHits;
  let listingSuggestionIndex = -1;
  const dropdownClasses = classNames(css.suggestionsDropdown, {
    [css.suggestionsDropdownExpanded]: isExpanded,
  });

  return (
    <div className={dropdownClasses} style={dropdownStyle}>
      <button
        type="button"
        className={classNames(css.suggestionsCloseButton, css.suggestionsDropdownCloseButton)}
        aria-label="Close search suggestions"
        onMouseDown={event => onSuggestionMouseDown(event)}
        onClick={onClose}
      >
        <IconClose rootClassName={css.suggestionsCloseIcon} />
      </button>
      <div className={css.suggestionsScrollable} ref={suggestionsScrollableRef}>
        {suggestions.map((suggestion, index) => {
          const querySuggestion = isQuerySuggestionItem(suggestion);
          if (!querySuggestion) {
            listingSuggestionIndex += 1;
          }

          const suggestionClasses = classNames(css.suggestionItem, {
            [css.suggestionItemActive]: index === activeSuggestionIndex,
            [css.querySuggestionItem]: querySuggestion,
          });
          const itemProps = getSuggestionItemProps ? getSuggestionItemProps(suggestion, index) : {};
          const sectionTitle = suggestion.__sectionTitle;

          return (
            <React.Fragment
              key={`${suggestion.__sourceId}-${suggestion.objectID || suggestion.query || index}`}
            >
              {sectionTitle ? (
                <div className={css.suggestionSectionTitle}>{sectionTitle}</div>
              ) : null}
              {querySuggestion ? (
                <button
                  type="button"
                  {...itemProps}
                  className={suggestionClasses}
                  onMouseDown={event => onSuggestionMouseDown(event)}
                  onClick={event => onSuggestionSelect(event, suggestion, index)}
                >
                  <div className={css.querySuggestionText}>
                    <div className={css.querySuggestionLabel}>Search for</div>
                    <div className={css.querySuggestionValue}>
                      {getQuerySuggestionText(suggestion)}
                    </div>
                  </div>
                </button>
              ) : (
                <a
                  {...itemProps}
                  className={suggestionClasses}
                  data-listing-suggestion-index={listingSuggestionIndex}
                  href={createResourceLocatorString(
                    'ListingPage',
                    routeConfiguration,
                    {
                      id: suggestion.objectID,
                      slug: suggestion.slug || createSlug(suggestion.title),
                    },
                    {}
                  )}
                  onMouseDown={event => onSuggestionMouseDown(event)}
                  onClick={event => onSuggestionSelect(event, suggestion, index)}
                >
                  {suggestion.imageUrl ? (
                    <img
                      className={css.suggestionImage}
                      src={suggestion.imageUrl}
                      alt={suggestion.title}
                    />
                  ) : (
                    <div className={css.suggestionImagePlaceholder} />
                  )}
                  <div className={css.suggestionText}>
                    <div className={css.suggestionTitle}>{suggestion.title}</div>
                    <div className={css.suggestionMeta}>
                      {[
                        suggestion.designerLabel || suggestion.otherBrand || suggestion.designer,
                        suggestion.categoryLabel || suggestion.category,
                        suggestion.subCategoryLabel || suggestion.subCategory,
                      ]
                        .filter(Boolean)
                        .join(' / ')}
                    </div>
                  </div>
                </a>
              )}
            </React.Fragment>
          );
        })}
      </div>
      {canSeeMore || isLoadingMore ? (
        <button
          type="button"
          className={css.seeMoreButton}
          onMouseDown={event => onSuggestionMouseDown(event)}
          onClick={onSeeMore}
          disabled={isLoadingMore}
        >
          {isLoadingMore ? 'Searching products' : 'See more'}
        </button>
      ) : null}
    </div>
  );
};

const KeywordSearchField = props => {
  const {
    activeSuggestionIndex,
    handleInputBlur,
    handleInputChange,
    handleInputFocus,
    handleInputKeyDown,
    iconClass,
    inputRef,
    intl,
    isLoadingMore,
    isMobile,
    keywordSearchWrapperClasses,
    onSuggestionMouseDown,
    onSuggestionSelect,
    routeConfiguration,
    showSuggestions,
    suggestions,
    listingSuggestionCount,
    totalListingHits,
    isExpanded,
    onCloseSuggestions,
    onOutsideClick,
    onSeeMore,
    dropdownStyle,
    suggestionsScrollableRef,
    autocompleteInputProps,
    getSuggestionItemProps,
  } = props;
  return (
    <OutsideClickHandler
      rootClassName={keywordSearchWrapperClasses}
      onOutsideClick={onOutsideClick}
    >
      <button className={css.searchSubmit}>
        <div className={iconClass}>
          <IconSearchDesktop />
        </div>
      </button>

      <Field
        name="keywords"
        render={({ input, meta }) => {
          return (
            <input
              {...autocompleteInputProps}
              className={isMobile ? css.mobileInput : css.desktopInput}
              {...input}
              id={isMobile ? 'keyword-search-mobile' : 'keyword-search'}
              data-testid={isMobile ? 'keyword-search-mobile' : 'keyword-search'}
              ref={inputRef}
              type="text"
              placeholder={intl.formatMessage({
                id: 'TopbarSearchForm.placeholder',
              })}
              autoComplete="off"
              onBlur={event => handleInputBlur(event, autocompleteInputProps.onBlur)}
              onChange={event =>
                handleInputChange(event, input.onChange, autocompleteInputProps.onChange)
              }
              onFocus={event => handleInputFocus(event, autocompleteInputProps.onFocus)}
              onKeyDown={event => handleInputKeyDown(event, autocompleteInputProps.onKeyDown)}
            />
          );
        }}
      />
      {showSuggestions ? (
        <button
          type="button"
          className={classNames(css.suggestionsCloseButton, css.suggestionsInputCloseButton)}
          aria-label="Close search suggestions"
          onMouseDown={event => onSuggestionMouseDown(event)}
          onClick={onCloseSuggestions}
        >
          <IconClose rootClassName={css.suggestionsCloseIcon} />
        </button>
      ) : null}
      <AutocompleteSuggestions
        activeSuggestionIndex={activeSuggestionIndex}
        isLoadingMore={isLoadingMore}
        onSuggestionMouseDown={onSuggestionMouseDown}
        onSuggestionSelect={onSuggestionSelect}
        routeConfiguration={routeConfiguration}
        showSuggestions={showSuggestions}
        suggestions={suggestions}
        listingSuggestionCount={listingSuggestionCount}
        totalListingHits={totalListingHits}
        isExpanded={isExpanded}
        onClose={onCloseSuggestions}
        onSeeMore={onSeeMore}
        dropdownStyle={dropdownStyle}
        suggestionsScrollableRef={suggestionsScrollableRef}
        getSuggestionItemProps={getSuggestionItemProps}
      />
      {/* {isMobile ? ( */}
      <button className={css.searchSubmitMobile}>
        <div className={iconClass}>
          <IconSearchDesktop />
        </div>
      </button>
      {/* ) : null} */}
    </OutsideClickHandler>
  );
};

const LocationSearchField = props => {
  const { desktopInputRootClass, intl, isMobile, inputRef, onLocationChange } = props;
  return (
    <Field
      name="location"
      format={identity}
      render={({ input, meta }) => {
        const { onChange, ...restInput } = input;

        // Merge the standard onChange function with custom behaviur. A better solution would
        // be to use the FormSpy component from Final Form and pass this.onChange to the
        // onChange prop but that breaks due to insufficient subscription handling.
        // See: https://github.com/final-form/react-final-form/issues/159
        const searchOnChange = value => {
          onChange(value);
          onLocationChange(value);
        };

        return (
          <LocationAutocompleteInput
            className={isMobile ? css.mobileInputRoot : desktopInputRootClass}
            iconClassName={isMobile ? css.mobileIcon : css.desktopIcon}
            inputClassName={isMobile ? css.mobileInput : css.desktopInput}
            predictionsClassName={isMobile ? css.mobilePredictions : css.desktopPredictions}
            predictionsAttributionClassName={isMobile ? css.mobilePredictionsAttribution : null}
            placeholder={intl.formatMessage({ id: 'TopbarSearchForm.placeholder' })}
            closeOnBlur={!isMobile}
            inputRef={inputRef}
            input={{ ...restInput, onChange: searchOnChange }}
            meta={meta}
          />
        );
      }}
    />
  );
};

class TopbarSearchFormComponent extends Component {
  constructor(props) {
    super(props);
    // onChange is used for location search
    this.onChange = this.onChange.bind(this);
    // onSubmit is used for keywords search
    this.onSubmit = this.onSubmit.bind(this);
    this.handleKeywordInputChange = this.handleKeywordInputChange.bind(this);
    this.handleKeywordInputBlur = this.handleKeywordInputBlur.bind(this);
    this.handleKeywordInputFocus = this.handleKeywordInputFocus.bind(this);
    this.handleKeywordInputKeyDown = this.handleKeywordInputKeyDown.bind(this);
    this.submitCurrentKeywordSearch = this.submitCurrentKeywordSearch.bind(this);
    this.handleSuggestionMouseDown = this.handleSuggestionMouseDown.bind(this);
    this.handleSuggestionSelect = this.handleSuggestionSelect.bind(this);
    this.handleSeeMore = this.handleSeeMore.bind(this);
    this.fetchAutocompleteSuggestions = this.fetchAutocompleteSuggestions.bind(this);
    this.hideSuggestions = this.hideSuggestions.bind(this);
    this.clearAndHideSuggestions = this.clearAndHideSuggestions.bind(this);
    this.initializeAlgoliaAutocomplete = this.initializeAlgoliaAutocomplete.bind(this);
    this.destroyAlgoliaAutocomplete = this.destroyAlgoliaAutocomplete.bind(this);
    this.getAutocompleteInputProps = this.getAutocompleteInputProps.bind(this);
    this.getSuggestionItemProps = this.getSuggestionItemProps.bind(this);
    this.getSuggestionsDropdownStyle = this.getSuggestionsDropdownStyle.bind(this);
    this.updateMobileAutocompleteScrollLock = this.updateMobileAutocompleteScrollLock.bind(this);
    this.scrollToListingSuggestion = this.scrollToListingSuggestion.bind(this);
    this.searchAutocompleteData = this.searchAutocompleteData.bind(this);
    this.searchAutocompleteHits = this.searchAutocompleteHits.bind(this);
    this.waitForLatestAutocompleteQuery = this.waitForLatestAutocompleteQuery.bind(this);
    this.buildListingPath = this.buildListingPath.bind(this);
    this.clearSubmittedKeywords = this.clearSubmittedKeywords.bind(this);

    // Callback ref
    this.searchInput = null;
    this.setSearchInputRef = element => {
      this.searchInput = element;
    };
    this.searchFormElement = null;
    this.setSearchFormRef = element => {
      this.searchFormElement = element;
    };
    this.searchFormApi = null;
    this.suggestionsScrollable = null;
    this.setSuggestionsScrollableRef = element => {
      this.suggestionsScrollable = element;
    };

    this.blurTimeout = null;
    this.autocompleteTimeout = null;
    this.autocompleteRequestId = 0;
    this.autocompleteAbortController = null;
    this.autocompleteCache = new Map();
    this.autocompleteDataCache = new Map();
    this.autocompleteDataPromiseCache = new Map();
    this.algoliaAutocomplete = null;
    this.latestAutocompleteTotalHits = 0;
    this.completedAutocompleteQuery = null;
    this.mobileAutocompleteScrollLock = null;
    this.pendingScrollToListingIndex = null;
    this.isSearchInputActive = false;
    this.state = {
      activeSuggestionIndex: -1,
      isAutocompleteLoading: false,
      isLoadingMore: false,
      searchTerm: props.initialValues?.keywords || '',
      showSuggestions: false,
      suggestions: [],
      hasCompletedAutocompleteSearch: false,
      suggestionLimit: AUTOCOMPLETE_BATCH_SIZE,
      listingSuggestionCount: 0,
      totalListingHits: 0,
    };
  }

  componentDidMount() {
    this.initializeAlgoliaAutocomplete();
    window.addEventListener('resize', this.updateMobileAutocompleteScrollLock);
  }

  componentDidUpdate(prevProps) {
    const previousKeyword = prevProps.initialValues?.keywords || '';
    const nextKeyword = this.props.initialValues?.keywords || '';

    if (previousKeyword !== nextKeyword && nextKeyword !== this.state.searchTerm) {
      this.setState({ searchTerm: nextKeyword });
    }

    const wasKeywordsSearch = isMainSearchTypeKeywords(prevProps.appConfig);
    const isKeywordsSearch = isMainSearchTypeKeywords(this.props.appConfig);

    if (!wasKeywordsSearch && isKeywordsSearch) {
      this.initializeAlgoliaAutocomplete();
    } else if (wasKeywordsSearch && !isKeywordsSearch) {
      this.destroyAlgoliaAutocomplete();
    }

    if (
      isKeywordsSearch &&
      this.algoliaAutocomplete &&
      prevProps.searchParams !== this.props.searchParams &&
      this.isSearchInputActive
    ) {
      this.algoliaAutocomplete.refresh();
    }

    if (
      this.pendingScrollToListingIndex !== null &&
      !this.state.isAutocompleteLoading &&
      !this.state.isLoadingMore &&
      this.state.listingSuggestionCount > this.pendingScrollToListingIndex
    ) {
      const listingIndex = Math.min(
        this.pendingScrollToListingIndex + 1,
        this.state.listingSuggestionCount - 1
      );
      this.pendingScrollToListingIndex = null;
      const runAfterPaint =
        window.requestAnimationFrame || (callback => window.setTimeout(callback, 0));
      runAfterPaint(() => this.scrollToListingSuggestion(listingIndex));
    }

    this.updateMobileAutocompleteScrollLock();
  }

  componentWillUnmount() {
    window.clearTimeout(this.blurTimeout);
    window.clearTimeout(this.autocompleteTimeout);
    window.removeEventListener('resize', this.updateMobileAutocompleteScrollLock);
    if (this.autocompleteAbortController) {
      this.autocompleteAbortController.abort();
    }
    this.unlockMobileAutocompleteScroll();
    this.destroyAlgoliaAutocomplete();
  }

  onChange(location) {
    const { appConfig, onSubmit } = this.props;
    if (!isMainSearchTypeKeywords(appConfig) && location.selectedPlace) {
      // Note that we use `onSubmit` instead of the conventional
      // `handleSubmit` prop for submitting. We want to autosubmit
      // when a place is selected, and don't require any extra
      // validations for the form.
      onSubmit({ location });
      // blur search input to hide software keyboard
      this.searchInput?.blur();
    }
  }

  onSubmit(values) {
    const { appConfig, onSubmit } = this.props;
    if (isMainSearchTypeKeywords(appConfig)) {
      this.hideSuggestions();
      onSubmit({ keywords: values.keywords || this.state.searchTerm });
      this.clearSubmittedKeywords();
      // blur search input to hide software keyboard
      this.searchInput?.blur();
    }
  }

  handleKeywordInputChange(event, inputOnChange, autocompleteOnChange) {
    const value = event.target.value;
    this.isSearchInputActive = true;
    inputOnChange(event);

    if (autocompleteOnChange) {
      autocompleteOnChange(event);
      this.pendingScrollToListingIndex = null;
      this.completedAutocompleteQuery = null;
      this.setState({
        searchTerm: value,
        hasCompletedAutocompleteSearch: false,
        suggestionLimit: AUTOCOMPLETE_BATCH_SIZE,
      });
      return;
    }

    this.pendingScrollToListingIndex = null;
    this.completedAutocompleteQuery = null;
    this.setState({
      activeSuggestionIndex: -1,
      searchTerm: value,
      hasCompletedAutocompleteSearch: false,
      suggestionLimit: AUTOCOMPLETE_BATCH_SIZE,
    });
    this.fetchAutocompleteSuggestions(value);
  }

  handleKeywordInputBlur(event, autocompleteOnBlur) {
    this.isSearchInputActive = false;

    if (autocompleteOnBlur) {
      autocompleteOnBlur(event);
      return;
    }

    this.blurTimeout = window.setTimeout(() => {
      this.hideSuggestions();
    }, 150);
  }

  handleKeywordInputFocus(event, autocompleteOnFocus) {
    this.isSearchInputActive = true;

    if (autocompleteOnFocus) {
      autocompleteOnFocus(event);
    }

    window.clearTimeout(this.blurTimeout);
    const { suggestions, searchTerm } = this.state;
    if (suggestions.length > 0 && searchTerm.length >= AUTOCOMPLETE_MIN_LENGTH) {
      this.setState({ showSuggestions: true });
    }
  }

  handleKeywordInputKeyDown(event, autocompleteOnKeyDown) {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.submitCurrentKeywordSearch(event.target.value);
      return;
    }

    if (autocompleteOnKeyDown) {
      autocompleteOnKeyDown(event);
      return;
    }

    const { activeSuggestionIndex, suggestions } = this.state;
    if (!suggestions.length) {
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.setState({
        activeSuggestionIndex:
          activeSuggestionIndex >= suggestions.length - 1 ? 0 : activeSuggestionIndex + 1,
        showSuggestions: true,
      });
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.setState({
        activeSuggestionIndex:
          activeSuggestionIndex <= 0 ? suggestions.length - 1 : activeSuggestionIndex - 1,
        showSuggestions: true,
      });
    }

    if (event.key === 'Escape') {
      this.hideSuggestions();
    }
  }

  submitCurrentKeywordSearch(value) {
    const keywords = value || this.state.searchTerm;
    this.hideSuggestions();
    this.props.onSubmit({ keywords });
    this.clearSubmittedKeywords();
    this.searchInput?.blur();
  }

  handleSuggestionMouseDown(event) {
    window.clearTimeout(this.blurTimeout);
    if (event) {
      event.preventDefault();
    }
  }

  handleSuggestionSelect(event, suggestion, index) {
    event.preventDefault();

    if (!isQuerySuggestionItem(suggestion)) {
      this.props.history.push(this.buildListingPath(suggestion));
      this.hideSuggestions();
      this.searchInput?.blur();
      return;
    }

    const itemProps = this.algoliaAutocomplete
      ? this.getSuggestionItemProps(suggestion, index)
      : null;

    if (itemProps?.onClick) {
      itemProps.onClick(event);
      return;
    }

    const query = getQuerySuggestionText(suggestion);
    this.props.onSubmit({ keywords: query });
    this.clearSubmittedKeywords();

    this.hideSuggestions();
    this.searchInput?.blur();
  }

  clearSubmittedKeywords() {
    if (!this.props.clearKeywordsAfterSubmit) {
      return;
    }

    this.searchFormApi?.change('keywords', '');
    if (this.algoliaAutocomplete?.setQuery) {
      this.algoliaAutocomplete.setQuery('');
    }
    this.setState({ searchTerm: '' });
  }

  handleSeeMore() {
    const nextLimit = this.state.suggestionLimit + AUTOCOMPLETE_BATCH_SIZE;
    this.pendingScrollToListingIndex = this.state.listingSuggestionCount;
    this.setState({ suggestionLimit: nextLimit }, () => {
      if (this.algoliaAutocomplete) {
        this.setState({ isLoadingMore: true }, () => {
          this.algoliaAutocomplete.refresh();
        });
        return;
      }

      this.fetchAutocompleteSuggestions(this.state.searchTerm, nextLimit);
    });
  }

  hideSuggestions() {
    if (this.algoliaAutocomplete) {
      this.algoliaAutocomplete.setIsOpen(false);
    }

    this.setState({
      activeSuggestionIndex: -1,
      isAutocompleteLoading: false,
      showSuggestions: false,
    });
  }

  clearAndHideSuggestions(form) {
    window.clearTimeout(this.autocompleteTimeout);
    if (this.autocompleteAbortController) {
      this.autocompleteAbortController.abort();
      this.autocompleteAbortController = null;
    }

    if (this.algoliaAutocomplete?.setQuery) {
      this.algoliaAutocomplete.setQuery('');
    }

    if (this.algoliaAutocomplete) {
      this.algoliaAutocomplete.setIsOpen(false);
    }

    form.change('keywords', '');
    this.pendingScrollToListingIndex = null;
    this.latestAutocompleteTotalHits = 0;
    this.completedAutocompleteQuery = null;
    this.setState({
      activeSuggestionIndex: -1,
      isAutocompleteLoading: false,
      isLoadingMore: false,
      searchTerm: '',
      showSuggestions: false,
      suggestions: [],
      hasCompletedAutocompleteSearch: false,
      suggestionLimit: AUTOCOMPLETE_BATCH_SIZE,
      listingSuggestionCount: 0,
      totalListingHits: 0,
    });
  }

  scrollToListingSuggestion(listingIndex) {
    const scrollable = this.suggestionsScrollable;

    if (listingIndex === null || !scrollable) {
      return;
    }

    const target = scrollable.querySelector(`[data-listing-suggestion-index="${listingIndex}"]`);
    if (!target) {
      return;
    }

    const scrollableRect = scrollable.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();
    const scrollPadding = 8;
    const targetTop = targetRect.top - scrollableRect.top;
    const targetBottom = targetRect.bottom - scrollableRect.top;
    const visibleBottom = scrollable.clientHeight;
    let nextScrollTop = scrollable.scrollTop;

    if (targetTop < scrollPadding) {
      nextScrollTop = scrollable.scrollTop + targetTop - scrollPadding;
    } else if (targetBottom > visibleBottom - scrollPadding) {
      nextScrollTop = scrollable.scrollTop + targetBottom - visibleBottom + scrollPadding;
    } else {
      return;
    }

    if (scrollable.scrollTo) {
      scrollable.scrollTo({
        top: Math.max(nextScrollTop, 0),
        behavior: 'smooth',
      });
    } else {
      scrollable.scrollTop = Math.max(nextScrollTop, 0);
    }
  }

  buildListingPath(suggestion) {
    return createResourceLocatorString(
      'ListingPage',
      this.props.routeConfiguration,
      {
        id: suggestion.objectID,
        slug: suggestion.slug || createSlug(suggestion.title),
      },
      {}
    );
  }

  async waitForLatestAutocompleteQuery(query) {
    await new Promise(resolve => window.setTimeout(resolve, AUTOCOMPLETE_DELAY));
    return this.isSearchInputActive && query === (this.state.searchTerm || '').trim();
  }

  initializeAlgoliaAutocomplete() {
    if (
      this.algoliaAutocomplete ||
      !isMainSearchTypeKeywords(this.props.appConfig) ||
      typeof window === 'undefined'
    ) {
      return;
    }

    const autocompleteCore = window[AUTOCOMPLETE_GLOBAL];
    const createAutocomplete = autocompleteCore?.createAutocomplete;

    if (!createAutocomplete) {
      return;
    }

    this.algoliaAutocomplete = createAutocomplete({
      id: 'landing-search-autocomplete',
      initialState: { query: this.state.searchTerm || '' },
      shouldPanelOpen: ({ state }) => state.query.trim().length >= AUTOCOMPLETE_MIN_LENGTH,
      onSubmit: ({ state, event }) => {
        event.preventDefault();
        this.hideSuggestions();
        this.props.onSubmit({ keywords: state.query });
        this.clearSubmittedKeywords();
        this.searchInput?.blur();
      },
      onStateChange: ({ state }) => {
        const isLoading = state.status === 'loading' || state.status === 'stalled';
        const suggestions = (state.collections || []).reduce((acc, collection) => {
          const sectionTitle =
            collection?.source?.sourceId === QUERY_SUGGESTIONS_SOURCE_ID
              ? 'Suggested searches'
              : 'Products';

          const nextItems = (collection?.items || []).map((item, index) => ({
            ...item,
            __autocompleteSource: collection?.source || null,
            __sectionTitle: index === 0 ? sectionTitle : null,
            __sourceId: collection?.source?.sourceId,
          }));

          return [...acc, ...nextItems];
        }, []);
        const listingCollection = (state.collections || []).find(
          collection => collection?.source?.sourceId === LISTINGS_SOURCE_ID
        );
        const listingSuggestionCount = listingCollection?.items?.length || 0;
        const normalizedQuery = state.query.trim();
        const hasCompletedAutocompleteSearch =
          normalizedQuery.length >= AUTOCOMPLETE_MIN_LENGTH &&
          !isLoading &&
          this.completedAutocompleteQuery === normalizedQuery;

        this.setState(prevState => ({
          activeSuggestionIndex: state.activeItemId ?? -1,
          isAutocompleteLoading: isLoading && !prevState.isLoadingMore,
          isLoadingMore: prevState.isLoadingMore && isLoading,
          searchTerm: state.query,
          showSuggestions: state.isOpen,
          suggestions,
          hasCompletedAutocompleteSearch,
          listingSuggestionCount,
          totalListingHits: this.latestAutocompleteTotalHits || listingSuggestionCount,
        }));

        if (!isLoading) {
          this.setState({ isLoadingMore: false });
        }
      },
      getSources: ({ query }) => [
        {
          sourceId: QUERY_SUGGESTIONS_SOURCE_ID,
          getItems: async () => {
            const normalizedQuery = query.trim();

            if (
              !normalizedQuery ||
              normalizedQuery.length < AUTOCOMPLETE_MIN_LENGTH ||
              !this.isSearchInputActive ||
              !isClientSideAlgoliaConfigured()
            ) {
              return [];
            }

            const isLatestQuery = await this.waitForLatestAutocompleteQuery(normalizedQuery);
            if (!isLatestQuery) {
              return [];
            }

            try {
              const data = await this.searchAutocompleteData(
                normalizedQuery,
                this.state.suggestionLimit
              );
              return data?.querySuggestions?.hits || [];
            } catch (e) {
              return [];
            }
          },
          onSelect: ({ item, event, setIsOpen }) => {
            event.preventDefault();
            const queryValue = getQuerySuggestionText(item);
            this.props.onSubmit({ keywords: queryValue });
            this.clearSubmittedKeywords();
            setIsOpen(false);
            this.searchInput?.blur();
          },
        },
        {
          sourceId: LISTINGS_SOURCE_ID,
          getItems: async () => {
            const normalizedQuery = query.trim();
            const hitsPerPage = this.state.suggestionLimit;

            if (
              !normalizedQuery ||
              normalizedQuery.length < AUTOCOMPLETE_MIN_LENGTH ||
              !this.isSearchInputActive
            ) {
              return [];
            }

            const isLatestQuery = await this.waitForLatestAutocompleteQuery(normalizedQuery);
            if (!isLatestQuery) {
              return [];
            }

            try {
              const data = isClientSideAlgoliaConfigured()
                ? await this.searchAutocompleteData(normalizedQuery, hitsPerPage)
                : { listings: await this.searchAutocompleteHits(normalizedQuery, hitsPerPage) };
              const listings = data?.listings || {};
              const hits = listings?.hits || [];
              this.latestAutocompleteTotalHits = listings?.nbHits || hits.length;
              return hits;
            } catch (e) {
              return [];
            }
          },
          onSelect: ({ item, event, setIsOpen }) => {
            event.preventDefault();
            this.props.history.push(this.buildListingPath(item));
            setIsOpen(false);
            this.searchInput?.blur();
          },
        },
      ],
    });
  }

  destroyAlgoliaAutocomplete() {
    if (this.algoliaAutocomplete?.destroy) {
      this.algoliaAutocomplete.destroy();
    }

    this.algoliaAutocomplete = null;
  }

  getAutocompleteInputProps() {
    if (!this.algoliaAutocomplete) {
      return {};
    }

    return this.algoliaAutocomplete.getInputProps({
      inputElement: this.searchInput,
      formElement: this.searchFormElement,
    });
  }

  getSuggestionItemProps(suggestion, index) {
    if (!this.algoliaAutocomplete || !suggestion?.__autocompleteSource) {
      return {};
    }

    return this.algoliaAutocomplete.getItemProps({
      item: suggestion,
      source: suggestion.__autocompleteSource,
    });
  }

  getSuggestionsDropdownStyle() {
    if (typeof window === 'undefined' || !this.searchInput) {
      return null;
    }

    const inputRect = this.searchInput.getBoundingClientRect();
    return { '--mobileSuggestionsTop': `${Math.round(inputRect.bottom + 8)}px` };
  }

  isMobileAutocompleteViewport() {
    return (
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia(MOBILE_AUTOCOMPLETE_MEDIA_QUERY).matches
    );
  }

  updateMobileAutocompleteScrollLock() {
    const shouldLock = this.state.showSuggestions && this.isMobileAutocompleteViewport();

    if (shouldLock) {
      this.lockMobileAutocompleteScroll();
    } else {
      this.unlockMobileAutocompleteScroll();
    }
  }

  lockMobileAutocompleteScroll() {
    if (this.mobileAutocompleteScrollLock || typeof window === 'undefined') {
      return;
    }

    const { body, documentElement } = window.document;
    const scrollY = window.pageYOffset || documentElement.scrollTop || body.scrollTop || 0;

    this.mobileAutocompleteScrollLock = {
      scrollY,
      bodyPosition: body.style.position,
      bodyTop: body.style.top,
      bodyLeft: body.style.left,
      bodyRight: body.style.right,
      bodyWidth: body.style.width,
      bodyHeight: body.style.height,
      bodyOverflow: body.style.overflow,
      htmlOverflow: documentElement.style.overflow,
      htmlHeight: documentElement.style.height,
    };

    body.style.position = 'fixed';
    body.style.top = `${-scrollY}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';
    body.style.height = '100%';
    body.style.overflow = 'hidden';
    documentElement.style.overflow = 'hidden';
    documentElement.style.height = '100%';
  }

  unlockMobileAutocompleteScroll() {
    if (!this.mobileAutocompleteScrollLock || typeof window === 'undefined') {
      return;
    }

    const { body, documentElement } = window.document;
    const {
      scrollY,
      bodyPosition,
      bodyTop,
      bodyLeft,
      bodyRight,
      bodyWidth,
      bodyHeight,
      bodyOverflow,
      htmlOverflow,
      htmlHeight,
    } = this.mobileAutocompleteScrollLock;

    body.style.position = bodyPosition;
    body.style.top = bodyTop;
    body.style.left = bodyLeft;
    body.style.right = bodyRight;
    body.style.width = bodyWidth;
    body.style.height = bodyHeight;
    body.style.overflow = bodyOverflow;
    documentElement.style.overflow = htmlOverflow;
    documentElement.style.height = htmlHeight;
    this.mobileAutocompleteScrollLock = null;
    window.scrollTo(0, scrollY);
  }

  async searchAutocompleteData(normalizedValue, hitsPerPage) {
    const { searchParams = {} } = this.props;
    const query = new URLSearchParams({
      q: normalizedValue,
      hitsPerPage: `${hitsPerPage}`,
      querySuggestionsHitsPerPage: `${QUERY_SUGGESTION_BATCH_SIZE}`,
    });

    const activeFilters = {
      pub_department: searchParams?.pub_department || 'women',
      pub_category: searchParams?.pub_category,
      pub_subCategory: searchParams?.pub_subCategory,
      pub_designer: searchParams?.pub_designer,
      pub_colour: searchParams?.pub_colour,
      pub_size: searchParams?.pub_size,
      pub_condition: searchParams?.pub_condition,
      pub_material: searchParams?.pub_material,
    };

    Object.entries(activeFilters).forEach(([key, filterValue]) => {
      if (filterValue) {
        query.set(key, filterValue);
      }
    });

    if (searchParams?.price) {
      const [minPrice, maxPrice] = `${searchParams.price}`.split(',');
      if (minPrice) {
        query.set(
          'priceMinSubunits',
          convertUnitToSubUnit(minPrice, unitDivisor(this.props.appConfig.currency))
        );
      }
      if (maxPrice) {
        query.set(
          'priceMaxSubunits',
          convertUnitToSubUnit(maxPrice, unitDivisor(this.props.appConfig.currency))
        );
      }
    }

    const cacheKey = query.toString();
    const cachedResult = this.autocompleteDataCache.get(cacheKey);
    if (cachedResult) {
      this.completedAutocompleteQuery = normalizedValue;
      return cachedResult;
    }

    const cachedPromise = this.autocompleteDataPromiseCache.get(cacheKey);
    if (cachedPromise) {
      return cachedPromise;
    }

    const algoliaParams = {
      query: normalizedValue,
      hitsPerPage,
      querySuggestionsHitsPerPage: QUERY_SUGGESTION_BATCH_SIZE,
      ...activeFilters,
    };

    if (query.get('priceMinSubunits')) {
      algoliaParams.priceMinSubunits = query.get('priceMinSubunits');
    }
    if (query.get('priceMaxSubunits')) {
      algoliaParams.priceMaxSubunits = query.get('priceMaxSubunits');
    }

    const pendingRequest = searchAlgoliaAutocompleteClient(algoliaParams)
      .then(data => {
        this.completedAutocompleteQuery = normalizedValue;
        this.autocompleteDataCache.set(cacheKey, data);
        if (this.autocompleteDataCache.size > 30) {
          const oldestKey = this.autocompleteDataCache.keys().next().value;
          this.autocompleteDataCache.delete(oldestKey);
        }
        return data;
      })
      .finally(() => {
        this.autocompleteDataPromiseCache.delete(cacheKey);
      });

    this.autocompleteDataPromiseCache.set(cacheKey, pendingRequest);
    return pendingRequest;
  }

  async searchAutocompleteHits(normalizedValue, hitsPerPage) {
    const { searchParams = {} } = this.props;
    const query = new URLSearchParams({
      q: normalizedValue,
      hitsPerPage: `${hitsPerPage}`,
      sort: AUTOCOMPLETE_LISTING_SORT,
    });

    const activeFilters = {
      pub_department: searchParams?.pub_department || 'women',
      pub_category: searchParams?.pub_category,
      pub_subCategory: searchParams?.pub_subCategory,
      pub_designer: searchParams?.pub_designer,
      pub_colour: searchParams?.pub_colour,
      pub_size: searchParams?.pub_size,
      pub_condition: searchParams?.pub_condition,
      pub_material: searchParams?.pub_material,
    };

    Object.entries(activeFilters).forEach(([key, filterValue]) => {
      if (filterValue) {
        query.set(key, filterValue);
      }
    });

    if (searchParams?.price) {
      const [minPrice, maxPrice] = `${searchParams.price}`.split(',');
      if (minPrice) {
        query.set(
          'priceMinSubunits',
          convertUnitToSubUnit(minPrice, unitDivisor(this.props.appConfig.currency))
        );
      }
      if (maxPrice) {
        query.set(
          'priceMaxSubunits',
          convertUnitToSubUnit(maxPrice, unitDivisor(this.props.appConfig.currency))
        );
      }
    }

    const cacheKey = query.toString();
    const cachedResult = this.autocompleteCache.get(cacheKey);
    if (cachedResult) {
      this.completedAutocompleteQuery = normalizedValue;
      return {
        hits: cachedResult.hits,
        nbHits: cachedResult.totalSuggestionHits,
      };
    }

    const algoliaParams = {
      query: normalizedValue,
      hitsPerPage,
      sort: AUTOCOMPLETE_LISTING_SORT,
      ...activeFilters,
    };

    if (query.get('priceMinSubunits')) {
      algoliaParams.priceMinSubunits = query.get('priceMinSubunits');
    }
    if (query.get('priceMaxSubunits')) {
      algoliaParams.priceMaxSubunits = query.get('priceMaxSubunits');
    }

    const data = isClientSideAlgoliaConfigured()
      ? await searchAlgoliaListingsClient(algoliaParams)
      : await window
        .fetch(
          `${apiBaseUrl(
            process.env.REACT_APP_MARKETPLACE_ROOT_URL
          )}/api/algolia/search?${query.toString()}`,
          {
            credentials: 'include',
          }
        )
        .then(async response => {
          if (!response.ok) {
            throw new Error(`Algolia autocomplete failed with status ${response.status}`);
          }

          return response.json();
        });

    this.completedAutocompleteQuery = normalizedValue;
    const nextSuggestions = data?.hits || [];
    this.autocompleteCache.set(cacheKey, {
      hits: nextSuggestions,
      totalSuggestionHits: data?.nbHits || nextSuggestions.length,
    });
    if (this.autocompleteCache.size > 30) {
      const oldestKey = this.autocompleteCache.keys().next().value;
      this.autocompleteCache.delete(oldestKey);
    }

    return data;
  }

  fetchAutocompleteSuggestions(value, limitOverride = null) {
    window.clearTimeout(this.autocompleteTimeout);
    if (this.autocompleteAbortController) {
      this.autocompleteAbortController.abort();
      this.autocompleteAbortController = null;
    }

    const normalizedValue = value?.trim() || '';

    if (!normalizedValue || normalizedValue.length < AUTOCOMPLETE_MIN_LENGTH) {
      this.setState({
        activeSuggestionIndex: -1,
        isAutocompleteLoading: false,
        isLoadingMore: false,
        showSuggestions: false,
        suggestions: [],
        hasCompletedAutocompleteSearch: false,
        listingSuggestionCount: 0,
        totalListingHits: 0,
      });
      return;
    }

    const hitsPerPage = limitOverride || this.state.suggestionLimit;

    const { searchParams = {} } = this.props;
    const query = new URLSearchParams({
      q: normalizedValue,
      hitsPerPage: `${hitsPerPage}`,
      sort: AUTOCOMPLETE_LISTING_SORT,
    });

    const activeFilters = {
      pub_department: searchParams?.pub_department || 'women',
      pub_category: searchParams?.pub_category,
      pub_subCategory: searchParams?.pub_subCategory,
      pub_designer: searchParams?.pub_designer,
      pub_colour: searchParams?.pub_colour,
      pub_size: searchParams?.pub_size,
      pub_condition: searchParams?.pub_condition,
      pub_material: searchParams?.pub_material,
    };

    Object.entries(activeFilters).forEach(([key, filterValue]) => {
      if (filterValue) {
        query.set(key, filterValue);
      }
    });

    if (searchParams?.price) {
      const [minPrice, maxPrice] = `${searchParams.price}`.split(',');
      if (minPrice) {
        query.set(
          'priceMinSubunits',
          convertUnitToSubUnit(minPrice, unitDivisor(this.props.appConfig.currency))
        );
      }
      if (maxPrice) {
        query.set(
          'priceMaxSubunits',
          convertUnitToSubUnit(maxPrice, unitDivisor(this.props.appConfig.currency))
        );
      }
    }

    const cacheKey = query.toString();
    const cachedResult = this.autocompleteCache.get(cacheKey);
    if (cachedResult) {
      this.setState({
        activeSuggestionIndex: -1,
        isAutocompleteLoading: false,
        isLoadingMore: false,
        showSuggestions: true,
        suggestions: cachedResult.hits,
        hasCompletedAutocompleteSearch: true,
        listingSuggestionCount: cachedResult.hits.length,
        totalListingHits: cachedResult.totalSuggestionHits,
      });
      return;
    }

    const requestId = this.autocompleteRequestId + 1;
    this.autocompleteRequestId = requestId;
    const isLoadingMore = hitsPerPage > AUTOCOMPLETE_BATCH_SIZE;
    this.setState({
      isAutocompleteLoading: !isLoadingMore,
      isLoadingMore,
      hasCompletedAutocompleteSearch: false,
      showSuggestions: true,
    });

    this.autocompleteTimeout = window.setTimeout(async () => {
      try {
        this.autocompleteAbortController = new AbortController();

        const algoliaParams = {
          query: normalizedValue,
          hitsPerPage,
          sort: AUTOCOMPLETE_LISTING_SORT,
          signal: this.autocompleteAbortController.signal,
          ...activeFilters,
        };

        if (query.get('priceMinSubunits')) {
          algoliaParams.priceMinSubunits = query.get('priceMinSubunits');
        }
        if (query.get('priceMaxSubunits')) {
          algoliaParams.priceMaxSubunits = query.get('priceMaxSubunits');
        }

        const data = isClientSideAlgoliaConfigured()
          ? await searchAlgoliaListingsClient(algoliaParams)
          : await window
            .fetch(
              `${apiBaseUrl(
                process.env.REACT_APP_MARKETPLACE_ROOT_URL
              )}/api/algolia/search?${query.toString()}`,
              {
                credentials: 'include',
                signal: this.autocompleteAbortController.signal,
              }
            )
            .then(async response => {
              if (!response.ok) {
                throw new Error(`Algolia autocomplete failed with status ${response.status}`);
              }

              return response.json();
            });
        if (requestId !== this.autocompleteRequestId) {
          return;
        }

        const nextSuggestions = data?.hits || [];
        this.autocompleteCache.set(cacheKey, {
          hits: nextSuggestions,
          totalSuggestionHits: data?.nbHits || nextSuggestions.length,
        });
        if (this.autocompleteCache.size > 30) {
          const oldestKey = this.autocompleteCache.keys().next().value;
          this.autocompleteCache.delete(oldestKey);
        }

        this.setState({
          activeSuggestionIndex: -1,
          isAutocompleteLoading: false,
          isLoadingMore: false,
          showSuggestions: true,
          suggestions: nextSuggestions,
          hasCompletedAutocompleteSearch: true,
          listingSuggestionCount: nextSuggestions.length,
          totalListingHits: data?.nbHits || nextSuggestions.length,
        });
      } catch (e) {
        if (e?.name === 'AbortError') {
          return;
        }

        if (requestId !== this.autocompleteRequestId) {
          return;
        }

        this.setState({
          activeSuggestionIndex: -1,
          isAutocompleteLoading: false,
          isLoadingMore: false,
          showSuggestions: false,
          suggestions: [],
          hasCompletedAutocompleteSearch: true,
          listingSuggestionCount: 0,
          totalListingHits: 0,
        });
      }
    }, AUTOCOMPLETE_DELAY);
  }

  render() {
    const { onSubmit, appConfig, routeConfiguration, ...restOfProps } = this.props;
    const {
      activeSuggestionIndex,
      isLoadingMore,
      searchTerm,
      showSuggestions,
      suggestions,
      listingSuggestionCount,
      totalListingHits,
    } = this.state;
    const isKeywordsSearch = isMainSearchTypeKeywords(appConfig);
    const submit = isKeywordsSearch ? this.onSubmit : onSubmit;
    const autocompleteInputProps = isKeywordsSearch ? this.getAutocompleteInputProps() : {};
    return (
      <FinalForm
        {...restOfProps}
        onSubmit={submit}
        render={formRenderProps => {
          const {
            rootClassName,
            className,
            desktopInputRoot,
            form,
            intl,
            isMobile,
            handleSubmit,
          } = formRenderProps;
          const classes = classNames(rootClassName, className);
          const desktopInputRootClass = desktopInputRoot || css.desktopInputRoot;
          this.searchFormApi = form;

          // Location search: allow form submit only when the place has changed
          const preventFormSubmit = e => e.preventDefault();
          const submitFormFn = isKeywordsSearch ? handleSubmit : preventFormSubmit;

          const keywordSearchWrapperClasses = classNames(
            css.keywordSearchWrapper,
            { [css.keywordSearchWrapperActive]: showSuggestions },
            isMobile ? css.mobileInputRoot : desktopInputRootClass
          );

          return (
            <Form
              className={classes}
              onSubmit={submitFormFn}
              enforcePagePreloadFor="SearchPage"
              contentRef={this.setSearchFormRef}
            >
              {isKeywordsSearch ? (
                <KeywordSearchField
                  keywordSearchWrapperClasses={keywordSearchWrapperClasses}
                  iconClass={classNames(isMobile ? css.mobileIcon : css.desktopIcon || css.icon)}
                  intl={intl}
                  isMobile={isMobile}
                  inputRef={this.setSearchInputRef}
                  activeSuggestionIndex={activeSuggestionIndex}
                  handleInputBlur={this.handleKeywordInputBlur}
                  handleInputChange={this.handleKeywordInputChange}
                  handleInputFocus={this.handleKeywordInputFocus}
                  handleInputKeyDown={this.handleKeywordInputKeyDown}
                  isLoadingMore={isLoadingMore}
                  onSuggestionMouseDown={this.handleSuggestionMouseDown}
                  onSuggestionSelect={this.handleSuggestionSelect}
                  routeConfiguration={routeConfiguration}
                  showSuggestions={showSuggestions}
                  suggestions={suggestions}
                  listingSuggestionCount={listingSuggestionCount}
                  totalListingHits={totalListingHits}
                  isExpanded={this.state.suggestionLimit > AUTOCOMPLETE_BATCH_SIZE}
                  onCloseSuggestions={() => this.clearAndHideSuggestions(form)}
                  onOutsideClick={
                    showSuggestions ? () => this.clearAndHideSuggestions(form) : identity
                  }
                  onSeeMore={this.handleSeeMore}
                  dropdownStyle={this.getSuggestionsDropdownStyle()}
                  suggestionsScrollableRef={this.setSuggestionsScrollableRef}
                  autocompleteInputProps={autocompleteInputProps}
                  getSuggestionItemProps={this.getSuggestionItemProps}
                />
              ) : (
                <LocationSearchField
                  desktopInputRootClass={desktopInputRootClass}
                  intl={intl}
                  isMobile={isMobile}
                  inputRef={this.setSearchInputRef}
                  onLocationChange={this.onChange}
                />
              )}
            </Form>
          );
        }}
      />
    );
  }
}

TopbarSearchFormComponent.defaultProps = {
  rootClassName: null,
  className: null,
  desktopInputRoot: null,
  isMobile: false,
  searchParams: null,
  clearKeywordsAfterSubmit: false,
};

TopbarSearchFormComponent.propTypes = {
  rootClassName: string,
  className: string,
  desktopInputRoot: string,
  onSubmit: func.isRequired,
  isMobile: bool,
  appConfig: object.isRequired,
  history: shape({
    push: func.isRequired,
  }).isRequired,
  routeConfiguration: arrayOf(propTypes.route).isRequired,
  searchParams: object,
  clearKeywordsAfterSubmit: bool,

  // from injectIntl
  intl: intlShape.isRequired,
};

const TopbarSearchForm = injectIntl(TopbarSearchFormComponent);

export default TopbarSearchForm;
